import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Navigation, ExternalLink, Mail } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import SEO, { breadcrumbSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import { SectionHead } from "../components/Cards";
import Reveal from "../components/Reveal";
import { SITE, waLink, IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal";

export default function Contact() {
  const [f, setF] = useState({ name: "", phone: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.message.trim()) {
      toast.error("Please fill your name, phone and message.");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, source: "contact_form" }),
      });
      if (!r.ok) throw new Error();
      toast.success("Enquiry sent! We usually reply within minutes on WhatsApp/call.");
      const msg = `Hello Vikash ji, I'm ${f.name} (${f.phone}). ${f.message}`;
      window.open(waLink(msg), "_blank", "noopener");
      setF({ name: "", phone: "", message: "", website: "" });
    } catch {
      toast.error("Could not send right now — please WhatsApp or call us directly.");
    }
    setSending(false);
  };

  return (
    <>
      <SEO
        title="Contact Us — Book a Taxi in Jodhpur"
        description="Contact Blue City Taxi Jodhpur: call 094602 05326 or WhatsApp for taxi booking. Address: Near Jhalamand, Navdurga Colony, Jhalamand, Jodhpur, Rajasthan 342016."
        path="/contact"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact Us", path: "/contact" }])]}
      />
      <PageHero
        title="Contact Blue City Taxi"
        subtitle="Call, WhatsApp or drop by — we're based in Jodhpur and available 24/7 for bookings and travel planning."
        image={IMAGES.clocktower || IMAGES.mehrangarh}
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact Us" }]}
      />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <SectionHead center={false} eyebrow="Reach Us" title="We're One Call Away" />
          <div className="mt-8 space-y-4">
            {[
              { icon: Phone, label: "Phone (24/7)", value: SITE.phoneDisplay, href: `tel:${SITE.phoneTel}`, tid: "contact-phone" },
              { icon: FaWhatsapp, label: "WhatsApp", value: "+91 94602 05326", href: waLink(), tid: "contact-whatsapp" },
              { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, tid: "contact-email" },
              { icon: MapPin, label: "Address", value: `${SITE.address1} ${SITE.address2}`, href: SITE.googleProfile, tid: "contact-address" },
            ].map((c) => (
              <Reveal key={c.label}>
                <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.tid} className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/80 p-5 hover:shadow-lg hover:border-royal/25 transition-all group">
                  <span className="w-12 h-12 rounded-xl bg-royal-50 flex items-center justify-center text-royal group-hover:bg-royal group-hover:text-gold transition-colors shrink-0">
                    <c.icon size={20} />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-400">{c.label}</span>
                    <span className="block font-heading font-bold text-navy mt-0.5">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={SITE.googleProfile} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-btn" className="inline-flex items-center gap-2 rounded-full bg-royal text-white text-sm font-bold px-6 py-3 hover:bg-royal-700 transition-colors">
              <Navigation size={15} /> Get Directions
            </a>
            <a href={SITE.googleProfile} target="_blank" rel="noopener noreferrer" data-testid="contact-google-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal text-sm font-bold px-6 py-3 hover:bg-royal hover:text-white transition-colors">
              <ExternalLink size={15} /> View Us on Google
            </a>
          </div>
          <Reveal className="mt-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Blue City Taxi location — Jhalamand, Jodhpur"
                src={SITE.mapEmbed}
                className="w-full h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-2xl shadow-xl shadow-navy/5 border border-slate-100 p-6 sm:p-8 lg:sticky lg:top-28">
            <h2 className="font-heading font-bold text-xl text-navy">Send an Enquiry</h2>
            <p className="text-[13px] text-slate-500 mt-1">We reply fast — usually within minutes during the day.</p>
            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="c-name" className="text-xs font-bold text-navy block mb-1.5">Your Name *</label>
                <input id="c-name" className={inputCls} placeholder="Full name" value={f.name} onChange={set("name")} data-testid="contact-name-input" required maxLength={80} />
              </div>
              <div>
                <label htmlFor="c-phone" className="text-xs font-bold text-navy block mb-1.5">Phone / WhatsApp Number *</label>
                <input id="c-phone" type="tel" className={inputCls} placeholder="10-digit mobile number" value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" required maxLength={15} />
              </div>
              <div>
                <label htmlFor="c-msg" className="text-xs font-bold text-navy block mb-1.5">Your Trip / Message *</label>
                <textarea id="c-msg" rows={4} className={inputCls} placeholder="e.g. Jodhpur to Udaipur, 2 people, 15th morning…" value={f.message} onChange={set("message")} data-testid="contact-message-input" required maxLength={1000} />
              </div>
              <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="w-full rounded-xl bg-gold text-navy font-heading font-extrabold py-3.5 tracking-wide hover:bg-gold-dark hover:text-white transition-colors disabled:opacity-60">
                {sending ? "SENDING…" : "SEND ENQUIRY"}
              </button>
              <p className="text-[11px] text-slate-400 text-center">Submitting also opens WhatsApp so you can continue the conversation instantly.</p>
            </div>
          </form>
        </Reveal>
      </section>
    </>
  );
}
